import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";

import { useAssetLookup } from "../../api/useAssetLookup";
import { formatGameTime } from "../../format/formatGameTime";
import type { TimelineEvent } from "../../map/TimelineEvent";
import { AssetIcon } from "../AssetIcon";

const DEATH = msg({ id: "analysis.map.event.death", message: "Your death" });
const KILLED_BY = msg({ id: "analysis.map.event.killed_by", message: "killed by {hero}" });
const NOT_HERO = msg({ id: "analysis.map.event.not_hero", message: "creeps or a tower" });
const DEAD_FOR = msg({ id: "analysis.map.event.dead_for", message: "dead for {seconds} s" });
const KILL = msg({ id: "analysis.map.event.kill", message: "Your kill" });
const BOSS = msg({ id: "analysis.map.event.boss", message: "Mid-boss killed" });
const BOSS_OURS = msg({ id: "analysis.map.event.boss_ours", message: "your team took the buff" });
const BOSS_ENEMY = msg({ id: "analysis.map.event.boss_enemy", message: "enemies took the buff" });
const RUNE = msg({ id: "analysis.map.event.rune", message: "Bridge rune" });
const RUNE_TAKEN = msg({ id: "analysis.map.event.rune_taken", message: "you took it" });
const PREVIOUS = msg({ id: "analysis.map.event.previous", message: "Previous event" });
const NEXT = msg({ id: "analysis.map.event.next", message: "Next event" });
const OBJECTIVES: Record<
  Extract<TimelineEvent, { kind: "objective" }>["objective"],
  { ours: MessageDescriptor; enemy: MessageDescriptor }
> = {
  guardian: {
    ours: msg({ id: "analysis.map.event.guardian_ours", message: "Our guardian destroyed" }),
    enemy: msg({ id: "analysis.map.event.guardian_enemy", message: "Enemy guardian destroyed" }),
  },
  walker: {
    ours: msg({ id: "analysis.map.event.walker_ours", message: "Our walker destroyed" }),
    enemy: msg({ id: "analysis.map.event.walker_enemy", message: "Enemy walker destroyed" }),
  },
  patron: {
    ours: msg({ id: "analysis.map.event.patron_ours", message: "Our patron destroyed" }),
    enemy: msg({ id: "analysis.map.event.patron_enemy", message: "Enemy patron destroyed" }),
  },
};

const DOTS: Record<TimelineEvent["kind"], string> = {
  death: "bg-map-missed",
  kill: "bg-map-kill",
  boss: "bg-map-boss",
  objective: "bg-muted",
  rune: "bg-map-kill",
};

type TimelineEventCardProps = {
  event: TimelineEvent;
  // Карточка закреплена (переход к событию): стрелки к соседним событиям; `null` — соседа нет.
  // Без них — карточка под курсором шкалы.
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
};

// Короткая карточка события шкалы на карте: время, что случилось, с кем.
export const TimelineEventCard = ({ event, onPrevious, onNext }: TimelineEventCardProps) => {
  const { _ } = useLingui();
  const findHero = useAssetLookup("heroes");
  const hero =
    event.kind === "death" && event.killerHeroId !== null
      ? findHero(event.killerHeroId)
      : event.kind === "kill"
        ? findHero(event.victimHeroId)
        : null;
  const [title, ...details]: string[] =
    event.kind === "death"
      ? [
          _(DEATH),
          hero === null ? _(NOT_HERO) : _({ ...KILLED_BY, values: { hero: hero.name } }),
          _({ ...DEAD_FOR, values: { seconds: event.deadForS } }),
        ]
      : event.kind === "kill"
        ? [_(KILL), hero?.name ?? ""]
        : event.kind === "boss"
          ? [_(BOSS), _(event.ours ? BOSS_OURS : BOSS_ENEMY)]
          : event.kind === "objective"
            ? [_(OBJECTIVES[event.objective][event.ours ? "ours" : "enemy"])]
            : [_(RUNE), _(RUNE_TAKEN)];
  const pinned = onPrevious !== undefined || onNext !== undefined;
  return (
    <div
      role="status"
      data-event={event.kind}
      className={cn(
        "flex min-w-44 items-center gap-2.5 rounded-[10px] border border-border-strong bg-surface/95 px-3 py-2 text-xs shadow-[0_8px_24px_rgb(0_0_0/0.45)]",
        pinned && "pointer-events-auto pr-2",
      )}
    >
      {hero !== null && <AssetIcon asset={hero} size={32} decorative className="rounded-md" />}
      <div className="flex min-w-0 flex-col">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold whitespace-nowrap text-foreground">
          <span
            aria-hidden="true"
            className={cn("size-2 shrink-0 rounded-full", DOTS[event.kind])}
          />
          <span className="font-mono text-foreground-soft">{formatGameTime(event.at)}</span>
          {title}
        </span>
        {details.filter(Boolean).map((detail) => (
          <span key={detail} className="text-muted">
            {detail}
          </span>
        ))}
      </div>
      {pinned && (
        <div className="ml-1 flex gap-1">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={_(PREVIOUS)}
            disabled={onPrevious === null || onPrevious === undefined}
            className="size-7"
            onClick={() => onPrevious?.()}
          >
            <ChevronLeft aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={_(NEXT)}
            disabled={onNext === null || onNext === undefined}
            className="size-7"
            onClick={() => onNext?.()}
          >
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      )}
    </div>
  );
};
